import { catchAny, catchWithDefaultAny } from '@vy/err';
import { defined, notDefined } from '@vy/null';
import { isEmptyString } from '@vy/string';
import { isEmptyArray } from '@vy/utils';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalSearchParams } from '../contexts/useSearchParamsContext';
import { getUserId } from '../services/user-auth';

const STORAGE_PREFIX = 'vy_page_filters_';

type PersistenceContext = {
  searchParamKeys: readonly string[];
  searchParams: URLSearchParams;
  storageKey: string;
};

/**
 * Persists a page's filter URL params to sessionStorage per user + pathname,
 * and restores them on mount when the URL has none of the tracked params.
 *
 * Two safeguards keep the restore from being clobbered:
 *  1. Call this hook AFTER all other hooks on the page (any other hook that
 *     internally uses `useStateParams` will write its initial state to URL via
 *     `setSearchParams` from a `useEffectOnce`; we want our effect to register
 *     last so it fires last).
 *  2. The first-mount restore runs inside `queueMicrotask` so it also lands
 *     AFTER every sibling `useEffectOnce` even if (1) is violated.
 *
 * Pass `searchParamKeys` as a stable, module-level `as const` array — it is
 * included in the effect deps, so a new array per render would re-run the
 * effect every time.
 */
export function usePageFiltersPersistence(searchParamKeys: readonly string[]) {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useGlobalSearchParams();
  const initKeyRef = useRef<string | null>(null);
  const restoreAttemptedRef = useRef<boolean>(false);

  useEffect(() => {
    const userId = getUserId();

    if (isEmptyString(userId)) {
      return;
    }

    const storageKey = getStorageKey(userId, pathname);
    const initKey = getInitKey(userId, pathname);
    const isFirstInit = initKeyRef.current !== initKey;
    const context: PersistenceContext = { searchParamKeys, searchParams, storageKey };

    if (isFirstInit) {
      initKeyRef.current = initKey;
      restoreAttemptedRef.current = false;

      queueMicrotask(() => {
        restoreAttemptedRef.current = true;

        const restoredSearchParams = tryRestoreFromSession(context);

        if (defined(restoredSearchParams)) {
          setSearchParams(restoredSearchParams);
          return;
        }

        persistToSession(context);
      });

      return;
    }

    // Skip persist while the restore microtask is still pending — otherwise the
    // StrictMode dev-mode second-invoke of this effect (or any sync re-render
    // before the microtask runs) would persist an empty URL state and wipe the
    // session entry we're about to restore from.
    if (!restoreAttemptedRef.current) {
      return;
    }

    persistToSession(context);
  }, [searchParams, pathname, searchParamKeys, setSearchParams]);
}

function tryRestoreFromSession({
  searchParamKeys,
  searchParams,
  storageKey,
}: PersistenceContext): URLSearchParams | null {
  const urlAlreadyHasTrackedParam = searchParamKeys.some((paramKey) => searchParams.has(paramKey));

  if (urlAlreadyHasTrackedParam) {
    return null;
  }

  const storedValue = readSessionItem(storageKey);

  if (isEmptyString(storedValue)) {
    return null;
  }

  const restoredSearchParams = new URLSearchParams(storedValue);
  const restoredEntries = searchParamKeys.flatMap((paramKey) => {
    const paramValue = restoredSearchParams.get(paramKey);

    return defined(paramValue) ? [[paramKey, paramValue] as const] : [];
  });

  if (isEmptyArray(restoredEntries)) {
    return null;
  }

  const nextSearchParams = new URLSearchParams(searchParams);

  for (const [paramKey, paramValue] of restoredEntries) {
    nextSearchParams.set(paramKey, paramValue);
  }

  return nextSearchParams;
}

function persistToSession({ searchParamKeys, searchParams, storageKey }: PersistenceContext) {
  const searchParamsToStore = new URLSearchParams();

  for (const paramKey of searchParamKeys) {
    const paramValue = searchParams.get(paramKey);

    if (defined(paramValue)) {
      searchParamsToStore.set(paramKey, paramValue);
    }
  }

  const serializedSearchParams = searchParamsToStore.toString();

  if (isEmptyString(serializedSearchParams)) {
    removeSessionItem(storageKey);
  } else {
    writeSessionItem(storageKey, serializedSearchParams);
  }
}

function getStorageKey(userId: string, pathname: string) {
  return `${STORAGE_PREFIX}${userId}:${pathname}`;
}

function getInitKey(userId: string, pathname: string) {
  return `${userId}:${pathname}`;
}

function getSessionStorage() {
  return catchWithDefaultAny<Storage | null>(() => window.sessionStorage, null);
}

function readSessionItem(storageKey: string) {
  const storage = getSessionStorage();

  if (notDefined(storage)) {
    return null;
  }

  return catchWithDefaultAny<string | null>(() => storage.getItem(storageKey), null);
}

function writeSessionItem(storageKey: string, storageValue: string) {
  const storage = getSessionStorage();

  if (notDefined(storage)) {
    return;
  }

  catchAny(() => storage.setItem(storageKey, storageValue));
}

function removeSessionItem(storageKey: string) {
  const storage = getSessionStorage();

  if (notDefined(storage)) {
    return;
  }

  catchAny(() => storage.removeItem(storageKey));
}
